import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate, Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  Play,
  Pause,
  Square,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Settings,
  History,
  Crosshair,
  AlertTriangle,
  Wallet,
  Archive,
  Info,
  Calendar,
  BarChart3,
  Copy,
  Shield,
  Target,
} from 'lucide-react';
import {
  useBot,
  useBotOpenPositions,
  useBotTrades,
  useBotTransactions,
  useBotCommands,
  useStartBot,
  useStopBot,
  useArchiveBot,
  useDepositBot,
  useWithdrawBot,
  useUpdateBot,
  type TradeHistoryParams,
} from '@/hooks/use-bots';
import { useBotSocket } from '@/hooks/use-socket';
import { useBotSignal } from '@/hooks/use-copy-trade';
import { useInterpolatedObject } from '@/hooks/use-interpolated-value';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { formatCurrency, botCurrency, formatPnl, formatPct, pnlColor, timeAgo } from '@/lib/format';
import { LiveChartSection } from '@/components/live-chart-section';
import { EquityCurve } from '@/components/equity-curve';
import { condToSentence, fmtLot, extractIndicators } from '@/lib/condition-text';
import type { BotInstance, TradeHistory, CapitalTransaction } from '@/types/api';

type Tab = 'overview' | 'activity' | 'settings';

export default function BotDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { data: bot, isLoading, error } = useBot(id!, { refetchInterval: 10_000 });
  const { data: botSignalInfo } = useBotSignal(id);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [currentPrice, setCurrentPrice] = useState<number | undefined>();
  const [pendingLimitOrders, setPendingLimitOrders] = useState<Array<{ orderId: string; type: 'BUY' | 'SELL'; limitPrice: number; volume: number }>>([]);

  // WebSocket: real-time updates
  const { on } = useBotSocket(id);

  useEffect(() => {
    if (!id) return;
    const unsubs = [
      // Per-tick state: merge directly into cache — no HTTP refetch
      on('bot:tick', (data: any) => {
        const prev = queryClient.getQueryData<BotInstance>(['bots', id]);
        if (!prev) return;
        // Trust TradeHistory-derived realized P&L for the balance. data.realized_pnl
        // comes from the engine's TradeRecordingService cache (same source the SL
        // uses); we fall back to the cached query data (itself enrichBotWithLivePnl)
        // if the engine field isn't there.
        //
        // We do NOT take Math.max(engine.virtual_balance, tradeBasedBalance) anymore.
        // For copy-trade bots, engine.virtual_balance is frozen at assigned_capital
        // (flushDirtyBots skips their balance fields), so Math.max would always pick
        // the stale value when realized P&L is negative. The "post-CLOSE_ALL flash"
        // concern that motivated Math.max is already handled by trade:event
        // invalidating the query → fresh fetch with correct realized_pnl in ~100ms.
        const realizedPnl = data.realized_pnl ?? prev.realized_pnl ?? prev.total_pnl;
        const tickBalance = (prev.assigned_capital ?? 0) + realizedPnl;
        const tickEquity = tickBalance + data.floating_pnl;

        queryClient.setQueryData<BotInstance>(['bots', id], {
          ...prev,
          virtual_equity: tickEquity,
          virtual_balance: tickBalance,
          floating_pnl: data.floating_pnl,
          combined_pnl: realizedPnl + data.floating_pnl,
          realized_pnl: realizedPnl,
          total_pnl: realizedPnl,
          used_margin: data.used_margin,
        });
        // Update current price for chart (bid = standard chart price)
        if (data.bid) setCurrentPrice(data.bid);
        // Track pending limit orders for chart display
        setPendingLimitOrders(data.pendingLimitOrders ?? []);
      }),
      // Structural events: invalidate to refetch full data
      on('bot:update', () => {
        queryClient.invalidateQueries({ queryKey: ['bots', id] });
      }),
      on('trade:event', () => {
        queryClient.invalidateQueries({ queryKey: ['bots', id] });
        queryClient.invalidateQueries({ queryKey: ['bots', id, 'trades'] });
        queryClient.invalidateQueries({ queryKey: ['bots', id, 'open-positions'] });
      }),
      on('position:update', () => {
        queryClient.invalidateQueries({ queryKey: ['bots', id, 'open-positions'] });
        queryClient.invalidateQueries({ queryKey: ['bots', id] });
      }),
      on('command:update', () => {
        queryClient.invalidateQueries({ queryKey: ['bots', id] });
      }),
      // Market feed: always-on price updates (works even when bot is stopped)
      on('market:tick', (data: any) => {
        if (data.bid) setCurrentPrice(data.bid);
      }),
    ];
    return () => unsubs.forEach((unsub) => unsub());
  }, [id, on, queryClient]);

  // Interpolate numeric fields for smooth 60fps stat transitions
  // Must be called before early returns to satisfy React's rules of hooks
  const interpolatedFields = useMemo(
    () => ['virtual_equity', 'virtual_balance', 'floating_pnl', 'combined_pnl', 'used_margin'] as const,
    [],
  );
  const smoothBot = useInterpolatedObject(bot, interpolatedFields as unknown as (keyof BotInstance)[], 300);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !bot) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load bot</p>
        <p className="text-xs text-ink-3">The bot may not exist or the API server is down.</p>
        <Link to="/bots" className="inline-block mt-3">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} />
            Back to Bots
          </Button>
        </Link>
      </div>
    );
  }

  const displayBot = smoothBot ?? bot;
  const currency = botCurrency(bot);

  const pnl = displayBot.combined_pnl ?? displayBot.total_pnl;
  const pnlPctValue = displayBot.assigned_capital > 0 ? (pnl / displayBot.assigned_capital) * 100 : 0;

  const isArchived = !!bot.archived_at;

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: 'Overview', icon: <BarChart3 size={14} /> },
    { key: 'activity', label: 'Activity', icon: <History size={14} /> },
    { key: 'settings', label: 'Settings', icon: <Settings size={14} /> },
  ];

  return (
    <div className="space-y-5">
      {/* Back Link */}
      <Link
        to={bot.strategy_type === 'COPY_TRADE' ? '/copy-trade' : '/bots'}
        className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
      >
        <ArrowLeft size={12} />
        {bot.strategy_type === 'COPY_TRADE' ? 'Copy Trading' : 'All Bots'}
      </Link>

      {/* Archived Banner */}
      {bot.archived_at && (
        <div className="glass-sm rounded-lg border border-amber/20 bg-amber/5 p-4 flex items-center gap-3">
          <Info size={18} className="text-amber flex-shrink-0" />
          <div>
            <p className="text-sm font-bold">This bot is archived</p>
            <p className="text-xs text-ink-3">
              Trade history and stats are preserved. Archived bots cannot be restarted or modified.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="glass-sm rounded-lg p-5">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            {bot.strategy_type === 'COPY_TRADE' ? (
              <img
                src={botSignalInfo?.signal?.avatar_url || `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(botSignalInfo?.signal?.name || bot.name)}&backgroundColor=f97316,fb923c,fbbf24&shape1Color=f97316,ea580c`}
                alt="" className="w-11 h-11 rounded-full bg-surface flex-shrink-0"
              />
            ) : (
              <div className="w-11 h-11 rounded-md bg-surface-2 flex items-center justify-center flex-shrink-0">
                {bot.archived_at ? (
                  <Archive size={20} className="text-ink-4" />
                ) : bot.is_active ? (
                  <Bot size={20} className={bot.botMode === 'PAPER' ? 'text-blue-400' : 'text-green'} />
                ) : (
                  <Bot size={20} className="text-ink-4" />
                )}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-extrabold tracking-tight">{bot.name}</h1>
                {bot.archived_at ? (
                  <Badge color="default">Archived</Badge>
                ) : (
                  <Badge color={bot.is_active ? (bot.botMode === 'PAPER' ? 'blue' : 'green') : 'default'}>
                    {bot.is_active ? 'Running' : 'Stopped'}
                  </Badge>
                )}
                {bot.botMode === 'CHALLENGE' ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-orange/10 text-orange border border-orange/20">Challenge</span>
                ) : bot.botMode === 'PAPER' ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">Paper</span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Live</span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge color="blue">{bot.symbol}</Badge>
                {bot.strategy_type === 'COPY_TRADE' ? (
                  botSignalInfo?.signal ? (
                    <Link to={`/copy-trade/${botSignalInfo.signal.id}`} className="flex items-center gap-1 text-[10px] text-orange hover:underline">
                      <Copy size={10} /> Copying: {botSignalInfo.signal.name}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] text-ink-4">
                      <Copy size={10} /> Copy Trade
                    </span>
                  )
                ) : (
                  <span className="text-[10px] text-ink-4">
                    {bot.strategy_type === 'CONDITION_ENGINE'
                      ? 'Condition Engine'
                      : bot.strategy_type === 'GRID_MARTINGALE'
                        ? 'Grid Martingale'
                        : bot.strategy_type}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions — hide for stopped copy-trade bots (treated as archived) */}
          <BotActions
            botId={bot.id}
            botName={bot.name}
            isActive={bot.is_active}
            isArchived={!!bot.archived_at || (bot.strategy_type === 'COPY_TRADE' && !bot.is_active && botSignalInfo?.subscription?.status === 'STOPPED')}
            isCopyTrade={bot.strategy_type === 'COPY_TRADE'}
            mode={bot.botMode}
            unallocatedCapital={bot.mt5Account?.unallocated_capital ?? 0}
            assignedCapital={displayBot.assigned_capital}
            virtualBalance={displayBot.virtual_balance}
            virtualEquity={displayBot.virtual_equity}
            currency={currency}
          />
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          <MiniStat label="Balance" value={formatCurrency(displayBot.virtual_balance, currency)} />
          <MiniStat label="Equity" value={formatCurrency(displayBot.virtual_equity, currency)} />
          <MiniStat
            label="Free Margin"
            value={formatCurrency(Math.max(0, displayBot.virtual_equity - (displayBot.used_margin ?? 0)), currency)}
          />
          <MiniStat
            label="Combined P&L"
            value={formatPnl(pnl, currency)}
            valueColor={pnlColor(pnl)}
            sub={formatPct(pnlPctValue)}
            subColor={pnlColor(pnlPctValue)}
          />
          <MiniStat
            label="Floating P&L"
            value={formatPnl(displayBot.floating_pnl ?? 0, currency)}
            valueColor={pnlColor(displayBot.floating_pnl ?? 0)}
          />
          <MiniStat
            label="Realized P&L"
            value={formatPnl(bot.realized_pnl ?? bot.total_pnl, currency)}
            valueColor={pnlColor(bot.realized_pnl ?? bot.total_pnl)}
          />
        </div>
      </div>

      {/* Copy trading signal description */}
      {bot.strategy_type === 'COPY_TRADE' && botSignalInfo?.signal && (() => {
        const desc = botSignalInfo.signal.description
          || botSignalInfo.signal.provider?.description
          || botSignalInfo.signal.provider?.strategy_metadata?.description;
        return desc ? (
          <div className="glass-sm rounded-lg p-4">
            <p className="text-xs text-ink-3 whitespace-pre-line leading-relaxed">{desc}</p>
          </div>
        ) : null;
      })()}

      {/* Challenge warning */}
      {bot.botMode === 'CHALLENGE' && bot.is_active && (
        <div
          className="flex items-center gap-2.5 rounded-lg px-4 py-3"
          style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.2)' }}
        >
          <AlertTriangle size={14} className="text-orange flex-shrink-0" />
          <p className="text-[11px] text-ink-3 leading-relaxed">
            This bot is linked to an active <strong className="text-ink">ForgeAlpha Challenge</strong>. Stopping it will <strong className="text-red">fail the challenge</strong>.
          </p>
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex gap-1 bg-surface/50 rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer flex-1 justify-center ${
              activeTab === tab.key
                ? 'bg-orange-bg text-orange'
                : 'text-ink-3 hover:text-ink hover:bg-surface'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab bot={bot} onSwitchTab={setActiveTab} />}
      {activeTab === 'activity' && (
        <ActivityTab
          botId={bot.id}
          symbol={bot.symbol}
          isActive={bot.is_active}
          isArchived={isArchived}
          isCopyTrade={bot.strategy_type === 'COPY_TRADE'}
          currentPrice={currentPrice}
          strategyTimeframe={(bot.rules as any)?.market_data?.timeframe}
          on={on}
          currency={currency}
          pendingLimitOrders={pendingLimitOrders}
        />
      )}
      {activeTab === 'settings' && <SettingsTab bot={bot} botSignalInfo={botSignalInfo} />}
    </div>
  );
}

// ─── Bot Actions ────────────────────────────────────

function BotActions({
  botId,
  botName,
  isActive,
  isArchived,
  isCopyTrade,
  unallocatedCapital,
  assignedCapital,
  virtualBalance: _virtualBalance,
  virtualEquity,
  currency,
}: {
  botId: string;
  botName: string;
  isActive: boolean;
  isArchived: boolean;
  isCopyTrade?: boolean;
  mode: 'LIVE' | 'PAPER' | 'CHALLENGE';
  unallocatedCapital: number;
  assignedCapital: number;
  virtualBalance: number;
  virtualEquity: number;
  currency: string;
}) {
  const navigate = useNavigate();
  const startBot = useStartBot();
  const stopBot = useStopBot();
  const archiveBotMutation = useArchiveBot();
  const [showStop, setShowStop] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const handleArchive = () => {
    archiveBotMutation.mutate(botId, {
      onSuccess: () => {
        toast.success(`"${botName}" archived`);
        navigate('/bots');
      },
      onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to archive bot'),
    });
  };

  // Archived bots: no actions
  if (isArchived) return null;

  return (
    <>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <div className="flex items-center gap-2">
          {isActive ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowStop(true)}
              disabled={stopBot.isPending}
            >
              {isCopyTrade ? <Pause size={12} /> : <Square size={12} />}
              {stopBot.isPending ? (isCopyTrade ? 'Pausing...' : 'Stopping...') : (isCopyTrade ? 'Pause' : 'Stop')}
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                onClick={() => startBot.mutate(botId)}
                disabled={startBot.isPending}
              >
                <Play size={12} />
                {startBot.isPending ? 'Starting...' : 'Start'}
              </Button>
              <button
                onClick={() => setShowArchive(true)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-ink-4 hover:text-amber hover:bg-amber/10 transition-colors cursor-pointer"
                title="Archive bot"
              >
                <Archive size={14} />
              </button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setShowDeposit(true)}>
            <ArrowDownToLine size={12} />
            <span className="hidden sm:inline">Deposit</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowWithdraw(true)}>
            <ArrowUpFromLine size={12} />
            <span className="hidden sm:inline">Withdraw</span>
          </Button>
        </div>
        {startBot.isError && (
          <p className="text-[10px] text-red">
            {(startBot.error as any)?.response?.data?.message || (startBot.error as Error)?.message || 'Failed to start bot'}
          </p>
        )}
      </div>

      {/* Stop Confirmation Modal */}
      <StopModal
        open={showStop}
        onClose={() => setShowStop(false)}
        onConfirm={() => {
          stopBot.mutate(botId);
          setShowStop(false);
        }}
        isPending={stopBot.isPending}
        isCopyTrade={isCopyTrade}
      />

      {/* Archive Confirmation Modal */}
      <Modal
        open={showArchive}
        onClose={() => !archiveBotMutation.isPending && setShowArchive(false)}
        title="Archive Bot"
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowArchive(false)}
              disabled={archiveBotMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleArchive}
              disabled={archiveBotMutation.isPending}
              variant="danger"
            >
              <Archive size={12} />
              {archiveBotMutation.isPending ? 'Archiving...' : 'Archive Bot'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold mb-1">Archive "{botName}"?</p>
              <p className="text-xs text-ink-3">
                This bot will be archived. All trade history and stats will be preserved
                and remain viewable. Assigned capital will be returned to the account.
                Archived bots cannot be restarted.
              </p>
            </div>
          </div>
          {archiveBotMutation.isError && (
            <div className="bg-red/5 border border-red/15 rounded-lg p-3">
              <p className="text-xs text-red font-semibold">
                {(archiveBotMutation.error as any)?.response?.data?.message || 'Failed to archive bot'}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Deposit Modal */}
      <AmountModal
        open={showDeposit}
        onClose={() => setShowDeposit(false)}
        title="Deposit Capital"
        actionLabel="Deposit"
        botId={botId}
        mode="deposit"
        availableBalance={unallocatedCapital}
        currentBotBalance={assignedCapital}
        currency={currency}
      />

      {/* Withdraw Modal */}
      <AmountModal
        open={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        title="Withdraw Capital"
        actionLabel="Withdraw"
        botId={botId}
        mode="withdraw"
        availableBalance={virtualEquity}
        currentBotBalance={assignedCapital}
        currency={currency}
      />

    </>
  );
}

function StopModal({
  open,
  onClose,
  onConfirm,
  isPending,
  isCopyTrade,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  isCopyTrade?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCopyTrade ? 'Pause Copying' : 'Stop Bot'}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={isPending}>
            {isCopyTrade ? <Pause size={12} /> : <Square size={12} />}
            {isPending
              ? (isCopyTrade ? 'Pausing...' : 'Stopping...')
              : (isCopyTrade ? 'Pause Copying' : 'Stop Bot')}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-amber flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold mb-1">Are you sure?</p>
          <p className="text-xs text-ink-3">
            {isCopyTrade
              ? 'This will pause copying and close all open positions. You can resume copying by restarting the bot.'
              : 'This will deactivate trading and close all open positions. The bot can be restarted later.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}

function AmountModal({
  open,
  onClose,
  title,
  actionLabel,
  botId,
  mode,
  availableBalance,
  currentBotBalance,
  currency,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  actionLabel: string;
  botId: string;
  mode: 'deposit' | 'withdraw';
  availableBalance: number;
  currentBotBalance: number;
  currency: string;
}) {
  const [amount, setAmount] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const deposit = useDepositBot();
  const withdraw = useWithdrawBot();
  const mutation = mode === 'deposit' ? deposit : withdraw;

  // Reset error/warning when amount changes
  useEffect(() => { mutation.reset(); setWarning(null); }, [amount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for open positions when withdrawing
  const { data: openPositions } = useBotOpenPositions(botId, {
    refetchInterval: undefined, // no polling needed — just check once when modal opens
  });
  const hasOpenPositions = mode === 'withdraw' && (openPositions?.length ?? 0) > 0;

  const parsedAmount = parseFloat(amount);
  // Use small epsilon (1 cent) to avoid floating-point false positives (e.g. 10000.00 > 9999.9999)
  const isOverLimit = !isNaN(parsedAmount) && parsedAmount > availableBalance + 0.01;

  const handleSubmit = () => {
    if (isNaN(parsedAmount) || parsedAmount <= 0 || isOverLimit) return;
    mutation.mutate(
      { id: botId, amount: parsedAmount },
      {
        onSuccess: (data: any) => {
          if (data?._warning) {
            setWarning(data._warning);
            setAmount('');
          } else {
            setAmount('');
            setWarning(null);
            onClose();
            toast.success(
              mode === 'deposit'
                ? `Deposited ${formatCurrency(parsedAmount, currency)}`
                : `Withdrew ${formatCurrency(parsedAmount, currency)}`,
            );
          }
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message ?? `${mode === 'deposit' ? 'Deposit' : 'Withdrawal'} failed`);
        },
      },
    );
  };

  const handleMax = () => {
    if (availableBalance > 0) {
      setAmount(availableBalance.toFixed(2));
    }
  };

  const balanceLabel =
    mode === 'deposit' ? 'Available (unallocated)' : 'Available equity';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={mutation.isPending || !amount || parsedAmount <= 0 || isOverLimit}
          >
            {mutation.isPending ? 'Processing...' : actionLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Warning: bot was stopped due to insufficient margin */}
        {warning && (
          <div className="flex items-start gap-2 bg-red/10 border border-red/20 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="text-red flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-red font-semibold leading-relaxed">
              {warning}
            </p>
          </div>
        )}

        {/* Warning: open positions during withdrawal */}
        {hasOpenPositions && !warning && (
          <div className="flex items-start gap-2 bg-amber/10 border border-amber/20 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="text-amber flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber font-semibold leading-relaxed">
              This bot has {openPositions!.length} open position{openPositions!.length > 1 ? 's' : ''}.
              Withdrawing capital reduces available margin and may trigger the equity stop-loss.
            </p>
          </div>
        )}

        {/* Balance info */}
        <div className="bg-surface/50 rounded-lg px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">
              {balanceLabel}
            </span>
            <span className="text-sm font-bold font-mono text-ink">
              {formatCurrency(availableBalance, currency)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">
              Current bot balance
            </span>
            <span className="text-sm font-bold font-mono text-ink">
              {formatCurrency(currentBotBalance, currency)}
            </span>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label={`Amount (${currency})`}
                type="number"
                placeholder="0.00"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={
                  isOverLimit
                    ? `Exceeds available ${formatCurrency(availableBalance, currency)}`
                    : mutation.isError
                      ? ((mutation.error as any)?.response?.data?.message ?? 'Transfer failed')
                      : undefined
                }
              />
            </div>
            <button
              type="button"
              onClick={handleMax}
              className="mb-[2px] text-[10px] font-bold text-orange hover:text-orange-light transition-colors cursor-pointer px-2 py-1.5 rounded bg-orange-bg/50 hover:bg-orange-bg"
            >
              MAX
            </button>
          </div>
        </div>

        {/* New bot balance preview */}
        {!isNaN(parsedAmount) && parsedAmount > 0 && !isOverLimit && (
          <div className="flex items-center justify-between bg-surface/50 rounded-lg px-3 py-2">
            <span className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">
              New bot balance
            </span>
            <span className="text-sm font-bold font-mono text-green">
              {formatCurrency(
                mode === 'deposit'
                  ? currentBotBalance + parsedAmount
                  : currentBotBalance - parsedAmount,
                currency,
              )}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Overview Tab ───────────────────────────────────

function OverviewTab({ bot, onSwitchTab }: { bot: BotInstance; onSwitchTab: (tab: Tab) => void }) {
  const currency = botCurrency(bot);
  const { data: tradeResult, isLoading: tradesLoading } = useBotTrades(bot.id, { limit: 100, status: 'CLOSED', refetchInterval: bot.is_active ? 10_000 : undefined });
  const { data: openPositions } = useBotOpenPositions(bot.id, { refetchInterval: bot.is_active ? 5_000 : undefined });
  const { data: recentTradesResult } = useBotTrades(bot.id, { limit: 5, refetchInterval: bot.is_active ? 10_000 : undefined });
  const trades = tradeResult?.trades ?? [];
  const recentTrades = recentTradesResult?.trades ?? [];
  const openPos = openPositions ?? [];

  // Build equity curve from closed trades + live trailing point
  const equityCurve = useMemo(() => {
    const points: { time: number; equity: number }[] = [
      { time: new Date(bot.created_at).getTime(), equity: bot.assigned_capital ?? 0 },
    ];
    if (trades.length > 0) {
      const sorted = [...trades].sort((a, b) => new Date(a.closed_at!).getTime() - new Date(b.closed_at!).getTime());
      let equity = bot.assigned_capital ?? 0;
      for (const t of sorted) {
        equity += (t.net_pnl ?? 0);
        points.push({ time: new Date(t.closed_at!).getTime(), equity });
      }
    }
    // Append live equity as trailing point (updates in real-time via WebSocket)
    if (!bot.archived_at) {
      const liveEquity = bot.virtual_equity ?? bot.assigned_capital ?? 0;
      points.push({ time: Date.now(), equity: liveEquity });
    }
    return points;
  }, [trades, bot.assigned_capital, bot.created_at, bot.virtual_equity, bot.archived_at]);

  const winRate = bot.total_trades > 0 ? (bot.winning_trades / bot.total_trades) * 100 : 0;
  const avgPnl = bot.total_trades > 0 ? (bot.realized_pnl ?? bot.total_pnl) / bot.total_trades : 0;

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="space-y-4">
      {/* Equity Curve */}
      {!tradesLoading && equityCurve.length > 1 && (
        <div className="glass-sm rounded-lg p-4">
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-2">
            Equity Curve
          </p>
          <EquityCurve data={equityCurve} initialCapital={bot.assigned_capital} height={200} />
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Total Trades" value={String(bot.total_trades)} />
        <MiniStat label="Win Rate" value={`${winRate.toFixed(1)}%`} valueColor={bot.total_trades === 0 ? undefined : winRate >= 50 ? 'text-green' : 'text-red'} />
        <MiniStat label="Winning" value={String(bot.winning_trades)} valueColor="text-green" />
        <MiniStat label="Losing" value={String(bot.losing_trades)} valueColor="text-red" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Avg Trade P&L" value={formatPnl(avgPnl, currency)} valueColor={pnlColor(avgPnl)} />
        <MiniStat label="Total Volume" value={`${bot.total_volume.toFixed(2)} lots`} />
        <MiniStat label="Assigned Capital" value={formatCurrency(bot.assigned_capital, currency)} />
        <MiniStat label="Virtual Balance" value={formatCurrency(bot.virtual_balance, currency)} />
      </div>

      {/* Side-by-side: Recent Trades + Activity Log */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Trades */}
        <div className="glass-sm rounded-lg overflow-hidden flex flex-col h-80">
          <div className="px-3 py-2 border-b border-glass-border flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Crosshair size={12} className="text-orange" />
              <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">Recent Trades</p>
            </div>
            <button
              onClick={() => onSwitchTab('activity')}
              className="text-[10px] text-ink-4 hover:text-orange transition-colors cursor-pointer"
            >
              View all &rarr;
            </button>
          </div>
          <div className="p-2 space-y-1.5 overflow-y-auto flex-1">
            {/* Open positions first */}
            {openPos.map((pos) => (
              <div
                key={pos.mt5_position_id}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md bg-surface/50"
              >
                <Badge color={pos.type === 'BUY' ? 'green' : 'red'} className="text-[9px] px-1.5 py-0">
                  {pos.type}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold">{pos.symbol}</span>
                    <span className="text-[9px] text-amber font-bold">OPEN</span>
                  </div>
                  <span className="text-[10px] text-ink-4 font-mono">
                    {pos.volume} lots · {formatPrice(pos.open_price, pos.symbol)}
                    {pos.current_price != null && ` → ${formatPrice(pos.current_price, pos.symbol)}`}
                  </span>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-[11px] font-bold font-mono ${pnlColor(pos.floating_pnl ?? 0)}`}>
                    {formatPnl(pos.floating_pnl ?? 0, currency)}
                  </p>
                </div>
              </div>
            ))}
            {/* Recent closed trades */}
            {recentTrades.filter(t => t.status === 'CLOSED').slice(0, 5).map((trade) => (
              <div
                key={trade.id}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md bg-surface/50"
              >
                <Badge color={trade.type === 'BUY' ? 'green' : 'red'} className="text-[9px] px-1.5 py-0">
                  {trade.type}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold">{trade.symbol}</span>
                    <span className="text-[9px] text-ink-4">CLOSED</span>
                  </div>
                  <span className="text-[10px] text-ink-4 font-mono">
                    {trade.volume} lots · {formatPrice(trade.open_price, trade.symbol)}
                    {trade.close_price ? ` → ${formatPrice(trade.close_price, trade.symbol)}` : ''}
                    {' · '}{timeAgo(trade.closed_at!)}
                  </span>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-[11px] font-bold font-mono ${pnlColor(trade.net_pnl)}`}>
                    {formatPnl(trade.net_pnl, currency)}
                  </p>
                </div>
              </div>
            ))}
            {openPos.length === 0 && recentTrades.length === 0 && (
              <div className="text-center py-6">
                <Crosshair size={20} className="text-ink-4 mx-auto mb-1.5" />
                <p className="text-[10px] text-ink-4">No trades yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div className="flex flex-col h-80">
          {bot.archived_at ? (
            <div className="glass-sm rounded-lg overflow-hidden flex flex-col h-full">
              <div className="px-3 py-2 border-b border-glass-border flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-ink-4" />
                  <p className="text-[10px] font-mono font-semibold text-ink-4 uppercase tracking-wider">Logs</p>
                </div>
                <Link
                  to={`/bots/${bot.id}/log`}
                  className="text-[10px] text-ink-4 hover:text-orange transition-colors"
                >
                  View all &rarr;
                </Link>
              </div>
              <div className="p-4 flex-1 flex items-center justify-center">
                <div className="flex items-center gap-4 flex-wrap text-xs">
                  <div className="flex items-center gap-1.5 text-ink-3">
                    <Calendar size={12} className="text-ink-4" />
                    <span>Created {fmtDate(bot.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-ink-3">
                    <Archive size={12} className="text-ink-4" />
                    <span>Archived {fmtDate(bot.archived_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <BotActivityLog botId={bot.id} createdAt={bot.created_at} currency={currency} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Bot Activity Log ───────────────────────────────

function BotActivityLog({ botId, createdAt, currency }: { botId: string; createdAt: string; currency: string; }) {
  const { data: cmdResult } = useBotCommands(botId, { limit: 10 });
  const { data: txResult } = useBotTransactions(botId, { limit: 10 });
  const { data: tradeResult } = useBotTrades(botId, { limit: 20 });

  const fmtTs = (d: string) => {
    const dt = new Date(d);
    const date = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const time = dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    return `${date} ${time}`;
  };

  const entries = useMemo(() => {
    const items: { time: string; label: string; detail?: string; color: string; level: string }[] = [];

    items.push({ time: createdAt, label: 'Bot Created', level: 'INFO', color: 'text-ink-4' });

    const cmdMap: Record<string, { label: string; level: string; color: string }> = {
      START: { label: 'Bot Started', level: 'INFO', color: 'text-green' },
      STOP: { label: 'Bot Stopped', level: 'WARN', color: 'text-red' },
      RESTART: { label: 'Bot Restarted', level: 'INFO', color: 'text-amber' },
      DEPOSIT: { label: 'Capital Deposited', level: 'INFO', color: 'text-green' },
      WITHDRAW: { label: 'Capital Withdrawn', level: 'WARN', color: 'text-red' },
      DRAIN: { label: 'Drain Activated', level: 'WARN', color: 'text-amber' },
    };
    for (const cmd of cmdResult?.commands ?? []) {
      if (cmd.status !== 'DONE') continue;
      const mapped = cmdMap[cmd.command] ?? { label: cmd.command.toLowerCase(), level: 'INFO', color: 'text-ink-3' };
      items.push({ time: cmd.processed_at ?? cmd.created_at, ...mapped });
    }

    for (const tx of txResult?.transactions ?? []) {
      items.push({
        time: tx.created_at,
        label: tx.type === 'DEPOSIT' ? 'Deposited Capital' : 'Withdrew Capital',
        detail: `amount=${formatCurrency(tx.amount, currency)} balance=${formatCurrency(tx.balance_after, currency)}`,
        level: 'INFO',
        color: tx.type === 'DEPOSIT' ? 'text-green' : 'text-red',
      });
    }

    for (const trade of tradeResult?.trades ?? []) {
      // Trade opened
      items.push({
        time: trade.opened_at,
        label: `Opened ${trade.type} Position`,
        detail: `vol=${trade.volume} price=${trade.open_price.toFixed(2)}${trade.trigger_reason_text ? ` reason="${trade.trigger_reason_text}"` : ''}`,
        level: 'INFO',
        color: trade.type === 'BUY' ? 'text-green' : 'text-red',
      });
      // Trade closed
      if (trade.status === 'CLOSED' && trade.closed_at) {
        const pnl = trade.net_pnl ?? trade.gross_pnl;
        items.push({
          time: trade.closed_at,
          label: `Closed ${trade.type} Position`,
          detail: `pnl=${pnl >= 0 ? '+' : ''}${formatCurrency(pnl, currency)}${trade.exit_reason_text ? ` reason="${trade.exit_reason_text}"` : ''}`,
          level: pnl < 0 ? 'WARN' : 'INFO',
          color: pnl >= 0 ? 'text-green' : 'text-red',
        });
      }
    }

    items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    return items.slice(0, 15);
  }, [cmdResult, txResult, tradeResult, createdAt, currency]);

  const levelColor = (level: string) =>
    level === 'WARN' ? 'text-amber' : level === 'ERROR' ? 'text-red' : 'text-ink-4';

  return (
    <div className="glass-sm rounded-lg overflow-hidden flex flex-col h-full">
      <div className="px-3 py-2 border-b border-glass-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green animate-pulse" />
          <p className="text-[10px] font-mono font-semibold text-ink-4 uppercase tracking-wider">Logs</p>
        </div>
        <Link
          to={`/bots/${botId}/log`}
          className="text-[10px] text-ink-4 hover:text-orange transition-colors"
        >
          View all &rarr;
        </Link>
      </div>
      <div className="bg-glass px-3 py-2 font-mono text-[11px] leading-relaxed overflow-y-auto flex-1">
        {entries.length === 0 ? (
          <p className="text-ink-4">No activity yet</p>
        ) : (
          entries.map((entry, i) => (
            <div key={i} className="flex gap-0 flex-wrap">
              <span className="text-ink-4 shrink-0">{fmtTs(entry.time)}</span>
              <span className={`mx-1.5 shrink-0 ${levelColor(entry.level)}`}>{entry.level.padEnd(4)}</span>
              <span className={entry.color}>{entry.label}</span>
              {entry.detail && <span className="text-ink-4 ml-1.5">{entry.detail}</span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Activity Tab ───────────────────────────────────

type ActivityView = 'positions' | 'trades' | 'transfers';

function ActivityTab({
  botId,
  symbol,
  isActive,
  isArchived,
  isCopyTrade,
  currentPrice,
  strategyTimeframe,
  on,
  currency,
  pendingLimitOrders,
}: {
  botId: string;
  symbol: string;
  isActive: boolean;
  isArchived: boolean;
  isCopyTrade?: boolean;
  currentPrice?: number;
  strategyTimeframe?: string;
  on: (event: string, handler: (...args: any[]) => void) => () => void;
  currency: string;
  pendingLimitOrders?: Array<{ orderId: string; type: 'BUY' | 'SELL'; limitPrice: number; volume: number }>;
}) {
  const [view, setView] = useState<ActivityView>(isArchived ? 'trades' : 'positions');
  const [typeFilter, setTypeFilter] = useState<TradeHistoryParams['type']>(undefined);
  const [tradePage, setTradePage] = useState(1);
  const [txPage, setTxPage] = useState(1);

  const { data: positions, isLoading: positionsLoading } = useBotOpenPositions(botId, { refetchInterval: 2_000 });
  const openPositions = positions ?? [];

  const { data: tradeResult, isLoading: tradesLoading } = useBotTrades(botId, {
    page: tradePage,
    limit: 50,
    type: typeFilter,
    status: 'CLOSED',
  });

  const { data: txResult, isLoading: txLoading } = useBotTransactions(botId, {
    page: txPage,
    limit: 20,
  });

  const trades = tradeResult?.trades ?? [];
  const tradeTotalPages = tradeResult?.totalPages ?? 1;
  const transactions = txResult?.transactions ?? [];
  const txTotalPages = txResult?.totalPages ?? 1;

  const views: { key: ActivityView; label: string }[] = [
    ...(!isArchived ? [{ key: 'positions' as ActivityView, label: `Positions${openPositions.length > 0 ? ` (${openPositions.length})` : ''}` }] : []),
    { key: 'trades', label: 'Trades' },
    { key: 'transfers', label: 'Transfers' },
  ];

  return (
    <div className="space-y-3">
      {/* View Toggle + Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-1">
          {views.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                view === v.key
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Trade-specific filters */}
        {view === 'trades' && (
          <div className="flex gap-1">
            {([undefined, 'BUY', 'SELL'] as const).map((t) => (
              <button
                key={t ?? 'all-type'}
                onClick={() => { setTypeFilter(t); setTradePage(1); }}
                className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all cursor-pointer ${
                  typeFilter === t
                    ? 'bg-orange-bg text-orange border border-orange/20'
                    : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2'
                }`}
              >
                {t ?? 'All'}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Positions View */}
      {view === 'positions' && !isArchived && (
        <div className="space-y-3">
          {/* Live Chart — hidden for copy-trade bots since the signal provider
              may trade symbols the copier's broker doesn't offer, so the chart
              symbol can't be trusted to match executed positions. */}
          {!isCopyTrade && (
            <LiveChartSection key={botId} botId={botId} symbol={symbol} isActive={isActive} openPositions={openPositions} pendingLimitOrders={pendingLimitOrders} currentPrice={currentPrice} strategyTimeframe={strategyTimeframe} on={on} currency={currency} />
          )}

          {/* Open Positions */}
          {positionsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : openPositions.length === 0 && (!pendingLimitOrders || pendingLimitOrders.length === 0) ? (
            <div className="glass-sm rounded-lg p-8 text-center">
              <Crosshair size={32} className="text-ink-4 mx-auto mb-2" />
              <p className="text-xs text-ink-3">No open positions</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Pending limit orders */}
              {pendingLimitOrders && pendingLimitOrders.map((lo) => (
                <div
                  key={lo.orderId}
                  className="glass-sm rounded-lg p-3 flex items-center gap-3 border border-dashed border-ink-4/30"
                >
                  <Badge color="default">{lo.type} LIMIT</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-ink-3">Pending</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-ink-4 font-mono">{lo.volume} lots</span>
                      <span className="text-[10px] text-ink-4">·</span>
                      <span className="text-[10px] text-ink-4 font-mono">
                        @ {lo.limitPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] text-ink-4 font-semibold">Waiting for fill</p>
                  </div>
                </div>
              ))}
              {/* Open positions */}
              {openPositions.map((pos) => (
                <div
                  key={pos.mt5_position_id}
                  className="glass-sm rounded-lg p-3 flex items-center gap-3"
                >
                  <Badge color={pos.type === 'BUY' ? 'green' : 'red'}>{pos.type}</Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold">{pos.symbol}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-ink-4 font-mono">{pos.volume} lots</span>
                      <span className="text-[10px] text-ink-4">·</span>
                      <span className="text-[10px] text-ink-4 font-mono">
                        @ {formatPrice(pos.open_price, pos.symbol)}
                      </span>
                    </div>
                  </div>
                  {pos.current_price != null && (
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] text-ink-4">Current</p>
                      <p className="text-xs font-mono font-semibold">
                        {formatPrice(pos.current_price, pos.symbol)}
                      </p>
                    </div>
                  )}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold font-mono ${pnlColor(pos.floating_pnl ?? 0)}`}>
                      {formatPnl(pos.floating_pnl ?? 0, currency)}
                    </p>
                    {pos.mt5_position_id && (
                      <p className="text-[10px] text-ink-4 font-mono">#{pos.mt5_position_id}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {(pos.floating_pnl ?? 0) >= 0 ? (
                      <TrendingUp size={14} className="text-green" />
                    ) : (
                      <TrendingDown size={14} className="text-red" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trades View */}
      {view === 'trades' && (
        <>
          {tradesLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : trades.length === 0 ? (
            <div className="glass-sm rounded-lg p-8 text-center">
              <History size={32} className="text-ink-4 mx-auto mb-2" />
              <p className="text-xs text-ink-3">No trades found</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                {trades.map((trade) => (
                  <TradeRow key={trade.id} trade={trade} currency={currency} />
                ))}
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                {tradePage > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => setTradePage((p) => p - 1)}>
                    ← Prev
                  </Button>
                )}
                <span className="text-[10px] text-ink-4 font-mono">
                  Page {tradePage} of {tradeTotalPages}
                </span>
                {tradePage < tradeTotalPages && (
                  <Button variant="ghost" size="sm" onClick={() => setTradePage((p) => p + 1)}>
                    Next →
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Transfers View */}
      {view === 'transfers' && (
        <>
          {txLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : transactions.length === 0 ? (
            <div className="glass-sm rounded-lg p-8 text-center">
              <Wallet size={32} className="text-ink-4 mx-auto mb-2" />
              <p className="text-xs text-ink-3">No deposit or withdrawal history</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                {transactions.map((tx) => (
                  <TransactionRow key={tx.id} tx={tx} currency={currency} />
                ))}
              </div>
              {txTotalPages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  {txPage > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => setTxPage((p) => p - 1)}>
                      ← Prev
                    </Button>
                  )}
                  <span className="text-[10px] text-ink-4 font-mono">
                    Page {txPage} of {txTotalPages}
                  </span>
                  {txPage < txTotalPages && (
                    <Button variant="ghost" size="sm" onClick={() => setTxPage((p) => p + 1)}>
                      Next →
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function TradeRow({ trade, currency }: { trade: TradeHistory; currency: string }) {
  return (
    <div className="glass-sm rounded-lg p-3 flex items-center gap-3">
      {/* Position # (MT5 position ID) */}
      {/* Direction */}
      <Badge color={trade.type === 'BUY' ? 'green' : 'red'}>{trade.type}</Badge>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold">{trade.symbol}</span>
          <Badge color={trade.status === 'OPEN' ? 'amber' : 'default'}>
            {trade.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-[10px] text-ink-4 font-mono">{trade.volume} lots</span>
          <span className="text-[10px] text-ink-4">·</span>
          <span className="text-[10px] text-ink-4 font-mono">
            Open <span className="text-ink-3">{formatPrice(trade.open_price, trade.symbol)}</span>
          </span>
          {trade.status === 'CLOSED' && (
            <>
              <span className="text-[10px] text-ink-4">·</span>
              <span className="text-[10px] text-ink-4 font-mono">
                Close <span className="text-ink-3">{trade.close_price ? formatPrice(trade.close_price, trade.symbol) : '—'}</span>
              </span>
            </>
          )}
          <span className="text-[10px] text-ink-4">·</span>
          <span className="text-[10px] text-ink-4">
            {trade.closed_at ? timeAgo(trade.closed_at) : timeAgo(trade.opened_at)}
          </span>
        </div>
      </div>

      {/* P&L + Position # */}
      <div className="text-right flex-shrink-0">
        <p className={`text-sm font-bold font-mono ${pnlColor(trade.net_pnl)}`}>
          {formatPnl(trade.net_pnl, currency)}
        </p>
        {trade.mt5_position_id && (
          <p className="text-[10px] text-ink-4 font-mono">#{trade.mt5_position_id}</p>
        )}
      </div>
    </div>
  );
}

function TransactionRow({ tx, currency }: { tx: CapitalTransaction; currency: string }) {
  const isDeposit = tx.type === 'DEPOSIT';

  return (
    <div className="glass-sm rounded-lg p-3 flex items-center gap-3">
      {/* Icon */}
      <div
        className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
          isDeposit ? 'bg-green/10' : 'bg-red/10'
        }`}
      >
        {isDeposit ? (
          <ArrowDownToLine size={14} className="text-green" />
        ) : (
          <ArrowUpFromLine size={14} className="text-red" />
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge color={isDeposit ? 'green' : 'red'}>{tx.type}</Badge>
          {tx.note && (
            <span className="text-[10px] text-ink-4 truncate">{tx.note}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-ink-4">{timeAgo(tx.created_at)}</span>
          <span className="text-[10px] text-ink-4">·</span>
          <span className="text-[10px] text-ink-4 font-mono">
            Balance after: {formatCurrency(tx.balance_after, currency)}
          </span>
        </div>
      </div>

      {/* Amount */}
      <div className="text-right flex-shrink-0">
        <p
          className={`text-sm font-bold font-mono ${
            isDeposit ? 'text-green' : 'text-red'
          }`}
        >
          {isDeposit ? '+' : '−'}{formatCurrency(tx.amount, currency)}
        </p>
      </div>
    </div>
  );
}

// ─── Settings Tab ───────────────────────────────────

function RiskLimitsEditor({ bot }: { bot: BotInstance }) {
  const updateBot = useUpdateBot();
  const [editing, setEditing] = useState(false);
  const [sl, setSl] = useState(bot.sl_pct ? String(bot.sl_pct) : '');
  const [tp, setTp] = useState(bot.tp_pct ? String(bot.tp_pct) : '');

  useEffect(() => {
    setSl(bot.sl_pct ? String(bot.sl_pct) : '');
    setTp(bot.tp_pct ? String(bot.tp_pct) : '');
  }, [bot.sl_pct, bot.tp_pct]);

  const slNum = parseFloat(sl) || 0;
  const tpNum = parseFloat(tp) || 0;
  const slDollar = bot.assigned_capital * (slNum / 100);
  const tpDollar = bot.assigned_capital * (tpNum / 100);

  const handleSave = () => {
    const slVal = sl ? parseFloat(sl) : null;
    const tpVal = tp ? parseFloat(tp) : null;

    if (slVal !== null && (slVal < 5 || slVal > 95)) {
      toast.error('Stop Loss must be between 5% and 95%');
      return;
    }
    if (tpVal !== null && (tpVal < 5 || tpVal > 95)) {
      toast.error('Take Profit must be between 5% and 95%');
      return;
    }

    updateBot.mutate(
      { id: bot.id, sl_pct: slVal, tp_pct: tpVal },
      {
        onSuccess: () => {
          toast.success('Risk limits updated');
          setEditing(false);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || 'Failed to update');
        },
      },
    );
  };

  return (
    <div className="glass-sm rounded-lg">
      <div className="px-4 py-2.5 border-b border-glass-border flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Shield size={10} className="text-red" />
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">Risk Limits</p>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="text-[10px] font-semibold text-orange hover:text-orange-2 transition-colors cursor-pointer"
          >
            Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setEditing(false); setSl(bot.sl_pct ? String(bot.sl_pct) : ''); setTp(bot.tp_pct ? String(bot.tp_pct) : ''); }}
              className="text-[10px] font-semibold text-ink-3 hover:text-ink transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateBot.isPending}
              className="text-[10px] font-semibold text-orange hover:text-orange-2 transition-colors cursor-pointer disabled:opacity-50"
            >
              {updateBot.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="p-4 space-y-3">
        {editing ? (
          <>
            {/* Stop Loss - edit */}
            <div className="bg-surface/50 rounded-lg p-3 border border-red/10">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                  <Shield size={10} className="text-red" />
                  Equity Stop Loss
                </label>
                <span className="text-sm font-extrabold font-mono text-red min-w-[3ch] text-right">
                  {sl || '—'}%
                </span>
              </div>
              <p className="text-[9px] text-ink-4 mb-2.5">
                Bot stops when equity drops this percentage below assigned capital.
              </p>
              <input
                type="range"
                min="5"
                max="95"
                value={sl || '10'}
                onChange={(e) => setSl(e.target.value)}
                className="w-full accent-red h-1 rounded-full cursor-pointer"
              />
              <div className="flex justify-between mt-1">
                <span className="text-[8px] text-ink-4 font-mono">5%</span>
                <span className="text-[8px] text-ink-4 font-mono">95%</span>
              </div>
              {slDollar > 0 && (
                <div className="mt-2 pt-2 border-t border-red/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-ink-4">Estimated max loss</span>
                    <span className="text-[11px] font-bold font-mono text-red">
                      -${slDollar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit - edit */}
            <div className={`rounded-lg p-3 border transition-colors ${tp ? 'bg-surface/50 border-green/10' : 'bg-surface/30 border-glass-border/30'}`}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                  <Target size={10} className={tp ? 'text-green' : 'text-ink-4'} />
                  Equity Take Profit
                </label>
                <div className="flex items-center gap-2">
                  {tp && (
                    <span className="text-sm font-extrabold font-mono text-green min-w-[3ch] text-right">
                      {tp}%
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setTp(tp ? '' : '50')}
                    className={`w-8 h-4 rounded-full transition-colors cursor-pointer relative ${
                      tp ? 'bg-green' : 'bg-surface-2'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                        tp ? 'left-4' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>
              {!tp && (
                <p className="text-[9px] text-ink-4 mt-1">
                  No profit target — bot runs until manually stopped.
                </p>
              )}
              {tp && (
                <div className="mt-1">
                  <p className="text-[9px] text-ink-4 mb-2.5">
                    Bot stops when equity gains this percentage of assigned capital.
                  </p>
                  <input
                    type="range"
                    min="5"
                    max="95"
                    value={tp}
                    onChange={(e) => setTp(e.target.value)}
                    className="w-full accent-green h-1 rounded-full cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-[8px] text-ink-4 font-mono">5%</span>
                    <span className="text-[8px] text-ink-4 font-mono">95%</span>
                  </div>
                  {tpDollar > 0 && (
                    <div className="mt-2 pt-2 border-t border-green/5">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-ink-4">Estimated gain</span>
                        <span className="text-[11px] font-bold font-mono text-green">
                          +${tpDollar.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {bot.is_active && (
              <div className="flex items-start gap-2 bg-amber/5 border border-amber/15 rounded-lg px-3 py-2.5">
                <AlertTriangle size={13} className="text-amber shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber leading-relaxed">
                  This bot is currently running. Changes take effect on the next tick and may trigger an immediate stop if the new limits are already breached.
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            {/* Stop Loss - view */}
            <div className="bg-surface/50 rounded-lg p-3 border border-red/10">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                  <Shield size={10} className="text-red" />
                  Equity Stop Loss
                </span>
                <span className="text-sm font-extrabold font-mono text-red">
                  {bot.sl_pct ? `${bot.sl_pct}%` : 'Default (5%)'}
                </span>
              </div>
              {bot.sl_pct && bot.assigned_capital > 0 && (
                <div className="mt-2 pt-2 border-t border-red/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-ink-4">Estimated max loss</span>
                    <span className="text-[11px] font-bold font-mono text-red">
                      -${(bot.assigned_capital * bot.sl_pct / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit - view */}
            <div className={`rounded-lg p-3 border ${bot.tp_pct ? 'bg-surface/50 border-green/10' : 'bg-surface/30 border-glass-border/30'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-ink-3 flex items-center gap-1.5">
                  <Target size={10} className={bot.tp_pct ? 'text-green' : 'text-ink-4'} />
                  Equity Take Profit
                </span>
                <span className={`text-sm font-extrabold font-mono ${bot.tp_pct ? 'text-green' : 'text-ink-4'}`}>
                  {bot.tp_pct ? `${bot.tp_pct}%` : 'Disabled'}
                </span>
              </div>
              {bot.tp_pct && bot.assigned_capital > 0 && (
                <div className="mt-2 pt-2 border-t border-green/5">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-ink-4">Estimated gain</span>
                    <span className="text-[11px] font-bold font-mono text-green">
                      +${(bot.assigned_capital * bot.tp_pct / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AllocationModeEditor({ bot }: { bot: BotInstance }) {
  const updateBot = useUpdateBot();
  const [editing, setEditing] = useState(false);
  const [enabled, setEnabled] = useState(!!bot.allocation_mode);
  const [mode, setMode] = useState<'FIXED_LOT' | 'FIXED_MULT' | 'EQUITY_RATIO'>(bot.allocation_mode ?? 'FIXED_LOT');
  const [fixedLot, setFixedLot] = useState(bot.alloc_fixed_lot ? String(bot.alloc_fixed_lot) : '');
  const [multiplier, setMultiplier] = useState(bot.alloc_multiplier ? String(bot.alloc_multiplier) : '1');

  useEffect(() => {
    setEnabled(!!bot.allocation_mode);
    setMode(bot.allocation_mode ?? 'FIXED_LOT');
    setFixedLot(bot.alloc_fixed_lot ? String(bot.alloc_fixed_lot) : '');
    setMultiplier(bot.alloc_multiplier ? String(bot.alloc_multiplier) : '1');
  }, [bot.allocation_mode, bot.alloc_fixed_lot, bot.alloc_multiplier]);

  const spec = bot.mt5Account?.symbol_specs?.[bot.symbol];

  const handleSave = () => {
    if (!enabled) {
      updateBot.mutate(
        { id: bot.id, allocation_mode: null, alloc_fixed_lot: null, alloc_multiplier: null },
        {
          onSuccess: () => { toast.success('Allocation mode updated'); setEditing(false); },
          onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update'),
        },
      );
      return;
    }

    if (mode === 'FIXED_LOT') {
      const lot = parseFloat(fixedLot);
      if (isNaN(lot) || lot <= 0) { toast.error('Fixed lot must be a positive number'); return; }
      if (spec && lot < spec.minVolume) { toast.error(`Lot (${lot}) is below broker min (${spec.minVolume})`); return; }
      if (spec && lot > spec.maxVolume) { toast.error(`Lot (${lot}) exceeds broker max (${spec.maxVolume})`); return; }
      updateBot.mutate(
        { id: bot.id, allocation_mode: 'FIXED_LOT', alloc_fixed_lot: lot, alloc_multiplier: null },
        {
          onSuccess: () => { toast.success('Allocation mode updated'); setEditing(false); },
          onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update'),
        },
      );
    } else if (mode === 'FIXED_MULT') {
      const mult = parseFloat(multiplier);
      if (isNaN(mult) || mult <= 0) { toast.error('Multiplier must be a positive number'); return; }
      updateBot.mutate(
        { id: bot.id, allocation_mode: 'FIXED_MULT', alloc_multiplier: mult, alloc_fixed_lot: null },
        {
          onSuccess: () => { toast.success('Allocation mode updated'); setEditing(false); },
          onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update'),
        },
      );
    } else if (mode === 'EQUITY_RATIO') {
      const mult = parseFloat(multiplier);
      if (isNaN(mult) || mult <= 0) { toast.error('Multiplier must be a positive number'); return; }
      updateBot.mutate(
        { id: bot.id, allocation_mode: 'EQUITY_RATIO', alloc_multiplier: mult, alloc_fixed_lot: null },
        {
          onSuccess: () => { toast.success('Allocation mode updated'); setEditing(false); },
          onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update'),
        },
      );
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEnabled(!!bot.allocation_mode);
    setMode(bot.allocation_mode ?? 'FIXED_LOT');
    setFixedLot(bot.alloc_fixed_lot ? String(bot.alloc_fixed_lot) : '');
    setMultiplier(bot.alloc_multiplier ? String(bot.alloc_multiplier) : '1');
  };

  const MODES = [
    { key: 'FIXED_LOT' as const, label: 'Fixed Lots', desc: 'Every trade uses the same lot size' },
    { key: 'FIXED_MULT' as const, label: 'Fixed Multiple', desc: 'Scale strategy volume by a multiplier' },
    { key: 'EQUITY_RATIO' as const, label: 'Equity Ratio', desc: 'Scale proportionally to your capital' },
  ];

  return (
    <div className="glass-sm rounded-lg">
      <div className="px-4 py-2.5 border-b border-glass-border flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Copy size={10} className="text-violet" />
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">Allocation Mode</p>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="text-[10px] font-semibold text-orange hover:text-orange-2 transition-colors cursor-pointer"
          >
            Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="text-[10px] font-semibold text-ink-3 hover:text-ink transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateBot.isPending}
              className="text-[10px] font-semibold text-orange hover:text-orange-2 transition-colors cursor-pointer disabled:opacity-50"
            >
              {updateBot.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
      <div className="p-4">
        {editing ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-ink-3">Enable allocation override</span>
              <button
                type="button"
                onClick={() => setEnabled(!enabled)}
                className={`w-8 h-4 rounded-full transition-colors cursor-pointer relative ${
                  enabled ? 'bg-violet' : 'bg-surface-2'
                }`}
              >
                <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${enabled ? 'left-4' : 'left-0.5'}`} />
              </button>
            </div>

            {!enabled ? (
              <p className="text-[9px] text-ink-4">
                Direct — strategy's own lot sizing, no override.
              </p>
            ) : (
              <>
                {/* Mode cards */}
                <div className="grid grid-cols-2 gap-2">
                  {MODES.map((m) => {
                    const selected = mode === m.key;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setMode(m.key)}
                        className={`relative text-left rounded-lg border px-3 py-2.5 transition-all duration-200 cursor-pointer ${
                          selected
                            ? 'border-violet/40 bg-violet/5'
                            : 'border-glass-border bg-surface/50 hover:bg-surface hover:border-glass-border/80'
                        }`}
                      >
                        <div className={`absolute top-2 right-2 w-2 h-2 rounded-full transition-all ${
                          selected ? 'bg-violet' : 'bg-ink-4/30'
                        }`} />
                        <span className={`text-[11px] font-bold ${selected ? 'text-violet' : 'text-ink-3'}`}>
                          {m.label}
                        </span>
                        <p className="text-[9px] text-ink-4 mt-0.5 leading-tight">{m.desc}</p>
                      </button>
                    );
                  })}
                </div>

                {mode === 'FIXED_LOT' && (
                  <div>
                    <label className="block text-[10px] font-semibold text-ink-3 mb-1">Lot Size</label>
                    <input
                      type="number"
                      min="0.001"
                      step="0.01"
                      value={fixedLot}
                      onChange={(e) => setFixedLot(e.target.value)}
                      placeholder="0.01"
                      className="w-full bg-surface border border-glass-border rounded-md px-2.5 py-1.5 text-xs font-mono text-ink outline-none focus:border-violet/40 transition-colors"
                    />
                    {spec && (
                      <div className="flex items-center gap-2 mt-1.5 px-0.5">
                        <span className="text-[9px] text-ink-4">Broker range:</span>
                        <span className="text-[9px] font-mono font-semibold text-violet bg-violet/10 rounded px-1.5 py-0.5">
                          {spec.minVolume} — {spec.maxVolume}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {(mode === 'FIXED_MULT' || mode === 'EQUITY_RATIO') && (
                  <div>
                    <label className="block text-[10px] font-semibold text-ink-3 mb-1">Multiplier</label>
                    <input
                      type="number"
                      min="0.001"
                      step="any"
                      value={multiplier}
                      onChange={(e) => setMultiplier(e.target.value)}
                      placeholder="1.0"
                      className="w-full bg-surface border border-glass-border rounded-md px-2.5 py-1.5 text-xs font-mono text-ink outline-none focus:border-violet/40 transition-colors"
                    />
                    {multiplier && parseFloat(multiplier) > 0 && (
                      <p className="text-[9px] text-ink-4 mt-1.5 px-0.5">
                        Each position opened will be <span className="font-semibold text-ink-3">{multiplier}x</span> the size computed by the strategy.
                      </p>
                    )}
                  </div>
                )}

                {bot.is_active && (
                  <div className="flex items-start gap-2 bg-amber/5 border border-amber/15 rounded-lg px-3 py-2.5">
                    <AlertTriangle size={13} className="text-amber shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber leading-relaxed">
                      Bot is running. Changes apply to new trades only.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* View mode */
          bot.allocation_mode ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-ink-3">
                  {bot.allocation_mode === 'FIXED_LOT' ? 'Fixed Lots' : bot.allocation_mode === 'EQUITY_RATIO' ? 'Equity Ratio' : 'Fixed Multiple'}
                </span>
                <span className="text-xs font-bold font-mono text-violet">
                  {bot.allocation_mode === 'FIXED_LOT'
                    ? `${bot.alloc_fixed_lot ?? 0.01} lots`
                    : `${bot.alloc_multiplier ?? 1}x`}
                </span>
              </div>
              <p className="text-[9px] text-ink-4">
                {bot.allocation_mode === 'FIXED_LOT'
                  ? 'Every trade uses the same lot size'
                  : bot.allocation_mode === 'EQUITY_RATIO'
                    ? 'Scaled proportionally to your capital'
                    : 'Strategy volume scaled by multiplier'}
              </p>
            </div>
          ) : (
            <p className="text-[10px] text-ink-4">
              Direct — strategy's own lot sizing, no override.
            </p>
          )
        )}
      </div>
    </div>
  );
}

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="glass-sm rounded-lg">
      <div className="px-4 py-2.5 border-b border-glass-border">
        <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">{title}</p>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

function SettingsRow({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider">{label}</span>
      <span className="text-xs font-semibold font-mono text-ink">{value}</span>
    </div>
  );
}

function SettingsTab({ bot, botSignalInfo }: { bot: NonNullable<ReturnType<typeof useBot>['data']>; botSignalInfo?: any }) {
  const currency = botCurrency(bot);
  const rules = bot.rules as Record<string, any> | null;
  const entry = rules?.entry as Record<string, any> | undefined;
  const exit = rules?.exit as Record<string, any> | undefined;
  const grid = entry?.grid as Record<string, any> | undefined;
  const trailingStop = exit?.trailingStop as Record<string, any> | undefined;
  const marketData = rules?.market_data as Record<string, any> | undefined;
  const risk = rules?.risk as Record<string, any> | undefined;

  const entryRules = (entry?.rules ?? []) as Record<string, any>[];
  const exitRules = (exit?.rules ?? []) as Record<string, any>[];
  const indicators = rules ? extractIndicators(rules) : [];

  return (
    <div className="space-y-4">
      {/* Bot Info */}
      <SettingsSection title="Bot Info">
        <SettingsRow label="Name" value={bot.name} />
        <SettingsRow
          label="Mode"
          value={bot.botMode === 'PAPER' ? (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">Paper</span>
          ) : (
            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Live</span>
          )}
        />
        <SettingsRow
          label="MT5 Account"
          value={bot.mt5Account ? `${bot.mt5Account.login_id} @ ${bot.mt5Account.server}` : '—'}
        />
        <SettingsRow label="Magic Number" value={String(bot.magic_number)} />
        <SettingsRow
          label="Leverage"
          value={bot.botMode === 'PAPER'
            ? `1:${bot.leverage}`
            : bot.mt5Account
              ? `1:${bot.mt5Account.leverage}`
              : `1:${bot.leverage}`}
        />
        <SettingsRow label="Assigned Capital" value={formatCurrency(bot.assigned_capital, currency)} />
      </SettingsSection>

      {/* Risk Limits */}
      <RiskLimitsEditor bot={bot} />

      {/* Allocation Mode */}
      <AllocationModeEditor bot={bot} />

      {/* Strategy Overview */}
      <div className="glass-sm rounded-lg">
        <div className="px-4 py-2.5 border-b border-glass-border">
          <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">Strategy</p>
        </div>
        <div className="p-4 space-y-4">
          {/* Linked strategy + type */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              {bot.template ? (
                <>
                  <Link
                    to={`/strategies/${bot.template.id}`}
                    className="text-sm font-bold text-orange hover:underline"
                  >
                    {bot.template.name}
                  </Link>
                  {bot.templateVersion && (
                    <span className="text-[10px] font-mono text-ink-3">
                      v{bot.templateVersion.version_number}
                      {bot.template.latest_version === bot.templateVersion.version_number ? (
                        <span className="text-green ml-1">(latest)</span>
                      ) : (
                        <span className="text-amber ml-1">(v{bot.template.latest_version} available)</span>
                      )}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-xs text-ink-4">No linked strategy</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Badge color="orange">
                {bot.strategy_type === 'COPY_TRADE'
                  ? 'Copy Trade'
                  : bot.strategy_type === 'CONDITION_ENGINE'
                    ? 'Condition Engine'
                    : bot.strategy_type === 'GRID_MARTINGALE'
                      ? 'Grid Martingale'
                      : bot.strategy_type}
              </Badge>
              {bot.strategy_type === 'COPY_TRADE' && botSignalInfo?.signal && (
                <Link to={`/copy-trade/${botSignalInfo.signal.id}`} className="text-[10px] text-orange hover:underline flex items-center gap-1">
                  <Copy size={10} /> {botSignalInfo.signal.name}
                </Link>
              )}
              {marketData?.timeframe && <Badge color="blue">{marketData.timeframe}</Badge>}
            </div>
          </div>

          {/* Entry Rules */}
          {entryRules.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider mb-2">Entry Rules</p>
              <div className="space-y-2">
                {entryRules.map((rule, i) => (
                  <div key={rule.id ?? i} className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge color={rule.direction === 'BUY' ? 'green' : rule.direction === 'SELL' ? 'red' : 'blue'}>
                        {rule.direction ?? 'SIGNAL'}
                      </Badge>
                      {rule.maxPositions && (
                        <span className="text-[10px] text-ink-4">max {rule.maxPositions} pos</span>
                      )}
                      {rule.cooldownCandles && (
                        <span className="text-[10px] text-ink-4">{rule.cooldownCandles} bar cooldown</span>
                      )}
                    </div>
                    {rule.direction === 'SIGNAL' ? (
                      <div className="space-y-1 pl-3 border-l-2 border-glass-border">
                        {rule.buyCondition && (
                          <div className="flex items-start gap-1.5">
                            <Badge color="green">BUY</Badge>
                            <span className="text-[10px] leading-relaxed">{condToSentence(rule.buyCondition)}</span>
                          </div>
                        )}
                        {rule.sellCondition && (
                          <div className="flex items-start gap-1.5">
                            <Badge color="red">SELL</Badge>
                            <span className="text-[10px] leading-relaxed">{condToSentence(rule.sellCondition)}</span>
                          </div>
                        )}
                      </div>
                    ) : rule.conditions ? (
                      <div className="pl-3 border-l-2 border-glass-border">
                        <span className="text-[10px] leading-relaxed">{condToSentence(rule.conditions)}</span>
                      </div>
                    ) : null}
                    {rule.lotSizing && (
                      <div className="text-[10px] text-ink-3 pl-3">Lot: {fmtLot(rule.lotSizing)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exit Rules */}
          {exitRules.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider mb-2">Exit Rules</p>
              <div className="space-y-2">
                {exitRules.map((rule, i) => (
                  <div key={rule.id ?? i} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge color="amber">{rule.action ?? 'CLOSE_ALL'}</Badge>
                      {rule.closePct != null && rule.closePct < 100 && (
                        <span className="text-[10px] text-ink-4">{rule.closePct}% of each</span>
                      )}
                    </div>
                    {rule.conditions && (
                      <div className="pl-3 border-l-2 border-glass-border">
                        <span className="text-[10px] leading-relaxed">{condToSentence(rule.conditions)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grid / Trailing Stop / Risk */}
          {(grid?.enabled || trailingStop?.enabled || risk) && (
            <div>
              <p className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider mb-2">Risk & Execution</p>
              <div className="space-y-2">
                {grid?.enabled && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge color="violet">Grid</Badge>
                    <span className="text-[10px] text-ink-3 font-mono">
                      {grid.direction !== 'FOLLOW_ENTRY' && <>{grid.direction} &middot; </>}
                      step {grid.stepValue}{' '}
                      {grid.stepType === 'PERCENT' ? '%' : grid.stepType === 'ATR_MULTIPLE' ? 'ATR' : 'pips'}
                      {grid.maxGridLevels ? <> &middot; max {grid.maxGridLevels} levels</> : ''}
                    </span>
                    {grid.lotSizing && (
                      <span className="text-[10px] text-ink-4">&middot; {fmtLot(grid.lotSizing)}</span>
                    )}
                  </div>
                )}
                {trailingStop?.enabled && (
                  <div className="flex items-center gap-1.5">
                    <Badge color="cyan">Trailing Stop</Badge>
                    <span className="text-[10px] text-ink-3 font-mono">
                      {trailingStop.type === 'ATR_MULTIPLE'
                        ? `ATR\u00D7${trailingStop.value} (period ${trailingStop.atrPeriod ?? 14})`
                        : trailingStop.type === 'PERCENT'
                          ? `${trailingStop.value}%`
                          : `${trailingStop.value} pips`}
                    </span>
                  </div>
                )}
                {risk?.maxTotalPositions && (
                  <div className="flex items-center gap-1.5">
                    <Badge color="amber">Max Positions</Badge>
                    <span className="text-[10px] text-ink-3 font-mono">{risk.maxTotalPositions}</span>
                  </div>
                )}
                {risk?.riskFactor != null && risk.riskFactor !== 1 && (
                  <div className="flex items-center gap-1.5">
                    <Badge color="amber">Risk Factor</Badge>
                    <span className="text-[10px] text-ink-3 font-mono">{risk.riskFactor}x</span>
                  </div>
                )}
                {exit?.equityStopLossPct != null && (
                  <div className="flex items-center gap-1.5">
                    <Badge color="red">Equity SL</Badge>
                    <span className="text-[10px] text-ink-3 font-mono">{exit.equityStopLossPct}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Indicators */}
          {indicators.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider mb-1.5">Indicators</p>
              <div className="flex flex-wrap gap-1.5">
                {indicators.map((ind) => (
                  <Badge key={ind} color="violet">{ind}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Banker */}
      <SettingsSection title="Banker">
        <SettingsRow
          label="Status"
          value={bot.is_banker_active ? <Badge color="green">Active</Badge> : <Badge color="default">Inactive</Badge>}
        />
        {bot.is_banker_active && (
          <>
            <SettingsRow label="Target Equity" value={formatCurrency(bot.target_equity, currency)} />
            <SettingsRow label="Rescue Trigger" value={formatCurrency(bot.rescue_trigger, currency)} />
          </>
        )}
      </SettingsSection>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────

function MiniStat({
  label,
  value,
  valueColor,
  sub,
  subColor,
}: {
  label: string;
  value: string;
  valueColor?: string;
  sub?: string;
  subColor?: string;
}) {
  return (
    <div className="bg-surface/50 rounded-lg p-3">
      <p className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider mb-1">
        {label}
      </p>
      <p className={`text-sm font-bold font-mono tracking-tight ${valueColor ?? ''}`}>
        {value}
      </p>
      {sub && (
        <p className={`text-[10px] font-semibold mt-0.5 ${subColor ?? 'text-ink-4'}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

function formatPrice(price: number, symbol: string): string {
  // Gold and forex pairs typically use different decimal places
  const isGold = symbol.toUpperCase().includes('XAU');
  const isJpy = symbol.toUpperCase().includes('JPY');
  const decimals = isGold ? 2 : isJpy ? 3 : 5;
  return price.toFixed(decimals);
}
